import {useLocale} from '@aboutyou/i18n';
import {lazy, useHydrationDone} from '@aboutyou/lazy-imports';
import {LoadingPageSpinner} from '@aboutyou/ui-components';
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {InfoBanner} from '../../../client-services/Login/InfoBanner/InfoBanner';
import {useSavedUser} from '../AuthenticationModal/hooks/useSavedUser';
import {useAuthenticationOpenState} from '../hooks/useAuthenticationOpenState';
import {useIsAuthenticated} from '../hooks/useIsAuthenticated';
import {useLoginScreenLogger} from '../hooks/useLoginScreenLogger';
import {AuthMethod} from '../types';
import {getShowLoginAsDefault} from '../utility/getShowLoginAsDefault';
import {AuthenticationContexts} from './AuthenticationContexts';
import {LogLegacyFacebookStuckInPopup} from './LogLegacyFacebookStuckInPopup';

const AuthenticationModalLazy = lazy(
  () =>
    import('../AuthenticationModal/AuthenticationModal').then(mod => ({
      default: mod.AuthenticationModal,
    })),
  {fallback: <LoadingPageSpinner />},
);

const SSOUrlCallbackHandlerLazy = lazy(() =>
  import(
    '../AuthenticationModal/components/SSOButtonsFetcher/SSOUrlCallbackHandler'
  ).then(mod => ({
    default: mod.SSOUrlCallbackHandler,
  })),
);

const AuthenticationValidatorLazy = lazy(() =>
  import('../AuthenticationValidator/AuthenticationValidator').then(mod => ({
    default: mod.AuthenticationValidator,
  })),
);

export interface AuthenticationProps {
  context: AuthenticationContexts;
  /**
   * Called regardless of action type, runs whenever the user successfully exists the auth prompt
   */
  onSuccess?: VoidFunction;
  /**
   * Called if an error is thrown during the authentication process
   */
  onError?: (error: string) => void;
  /**
   * Called if the user cancels the auth process e.g. closes the modal
   */
  onCancel?: VoidFunction;
  /**
   * If true, the modal will display the guest option
   */
  allowGuestLogin?: boolean;
  /**
   * If provided, it will replace the current history entry when the modal opens
   * If not, a new entry will be added
   */
  replaceHistory?: boolean;
  /**
   * If provided, it will display login screen with the provided component at the top below the header
   */
  customBannerComponent?: React.ReactNode;
  /**
   * If provided, it will display login screen with the provided component at the top below the header
   */
  aboveHeaderBannerComponent?: React.ReactNode;
  /**
   * Disable toast shown on success. There are some situations where we dont want to show toast. Mainly mobile implementations of features
   */
  disableSuccessToast?: boolean;
  /**
   * redirect to checkout if apple login
   */
  forceRedirectToCheckoutOnSSOLoginSuccess?: boolean;
  /**
   * Force relogin modal in situations where user is logged in but session expired
   */
  forceSessionRefresh?: boolean;
  /**
   * needs CO Session to continue further
   */
  needsCheckoutSession: boolean;
}

interface AuthenticationConfig {
  showUserAuthenticationPrompt: (props: AuthenticationProps) => void;
}

const AuthenticationContext = createContext<AuthenticationConfig>(
  {} as AuthenticationConfig,
);

export const useAuthenticationPrompt = () => {
  const {showUserAuthenticationPrompt} = useContext(AuthenticationContext);

  if (!showUserAuthenticationPrompt) {
    console.warn(
      'Cannot find value for AuthenticationContext. Did you add a AuthenticationContext in one of the parent components?',
    );
    return {
      showUserAuthenticationPrompt: () => {},
    };
  }

  return {showUserAuthenticationPrompt} as AuthenticationConfig;
};

export const AuthenticationProvider = ({
  children,
  onSyncLegacy,
}: PropsWithChildren<{onSyncLegacy?: VoidFunction}>) => {
  return (
    <AuthenticationProviderBase onSyncLegacy={onSyncLegacy}>
      {children}
    </AuthenticationProviderBase>
  );
};

const isAuthMethod = (parameter: any): parameter is AuthMethod =>
  parameter === 'register' ||
  parameter === 'login' ||
  parameter === 'guest-register';

const AuthenticationProviderBase = ({
  children,
  onSyncLegacy,
}: PropsWithChildren<{onSyncLegacy?: VoidFunction}>) => {
  const loginContext = useRef<AuthenticationContexts | null>(null);
  const isHydrationDone = useHydrationDone();
  const onCancelRef = useRef<VoidFunction>(undefined);
  const onSuccessRef = useRef<VoidFunction>(undefined);
  const onErrorRef = useRef<(e: string) => void>(undefined);
  const allowGuestLoginRef = useRef<boolean>(undefined);
  const customBannerComponentRef = useRef<React.ReactNode>(undefined);
  const aboveHeaderBannerComponentRef = useRef<React.ReactNode>(undefined);
  const disableSuccessToastRef = useRef<boolean>(false);
  const forceRedirectToCheckoutOnAppleLoginSuccessRef = useRef<boolean>(false);

  const {isAuthenticated, isAuthenticatedCallback, isUserDataLoading} =
    useIsAuthenticated();
  const {isOpen, cancel, open, parameter, hide} = useAuthenticationOpenState({
    // We only want to enable the auto-open of the modal if the user is not authenticated
    enableParams: !isAuthenticated && !isUserDataLoading,
  });
  const {hasSavedUser, ...savedUser} = useSavedUser();
  const locale = useLocale();

  const [authMethod, setAuthMethod] = React.useState<AuthMethod>(
    isAuthMethod(parameter) ? parameter : 'login',
  );

  /**
   * We cannot just set the parameter as initial value
   * as its initially null
   */
  useEffect(() => {
    if (isAuthMethod(parameter)) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setAuthMethod(parameter);
    }
  }, [parameter]);

  const loginScreenLogger = useLoginScreenLogger();

  const onCancel = () => {
    cancel();
    onCancelRef.current?.();

    loginScreenLogger({
      event: 'cancelled',
      context: loginContext.current!,
    });
  };

  const onSuccess = () => {
    hide();
    onSuccessRef.current?.();
    onSyncLegacy?.();

    loginScreenLogger({
      event: 'login-success',
      context: loginContext.current!,
    });
  };

  const onError = (errorDetail: string) => {
    onErrorRef.current?.(errorDetail);

    loginScreenLogger({
      event: 'login-fail',
      context: loginContext.current!,
    });
  };

  const showUserAuthenticationPrompt = useCallback(
    async ({
      context,
      onCancel,
      onError,
      onSuccess,
      allowGuestLogin,
      replaceHistory,
      customBannerComponent,
      aboveHeaderBannerComponent,
      disableSuccessToast,
      forceRedirectToCheckoutOnSSOLoginSuccess = false,
      forceSessionRefresh,
      needsCheckoutSession,
    }: AuthenticationProps) => {
      const {isAuthenticated, reason} = await isAuthenticatedCallback({
        needsCheckoutSession,
      });
      // Do not open the modal if the user is already authenticated, when we call the prompt to open
      // If already authenticated, trigger the onSuccess callback if there is one
      if (isAuthenticated && !forceSessionRefresh) {
        onSuccess?.();
        return;
      }

      loginContext.current = context;
      onCancelRef.current = onCancel;
      onSuccessRef.current = onSuccess;
      onErrorRef.current = onError;
      allowGuestLoginRef.current = allowGuestLogin;
      customBannerComponentRef.current = customBannerComponent;
      aboveHeaderBannerComponentRef.current = aboveHeaderBannerComponent;
      disableSuccessToastRef.current = Boolean(disableSuccessToast);
      forceRedirectToCheckoutOnAppleLoginSuccessRef.current =
        forceRedirectToCheckoutOnSSOLoginSuccess;
      const shouldShowLoginAsDefault = getShowLoginAsDefault(locale);
      const resumeValidationFailed =
        isAuthenticated === false && reason === 'resume-validation';

      const parameter =
        forceSessionRefresh ||
        resumeValidationFailed ||
        shouldShowLoginAsDefault
          ? undefined
          : 'register';

      open({
        replaceHistory,
        parameter,
      });

      loginScreenLogger({
        event: 'opened',
        context,
      });
    },
    [isAuthenticatedCallback, locale, loginScreenLogger, open],
  );

  const onAuthenticationMethodChange = (type: AuthMethod) => {
    setAuthMethod(type);
  };

  const value = useMemo(
    () => ({showUserAuthenticationPrompt}),
    [showUserAuthenticationPrompt],
  );

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
      {isHydrationDone && (
        <AuthenticationModalLazy
          allowGuestLogin={allowGuestLoginRef.current}
          onError={onError}
          onSuccess={onSuccess}
          onCancel={onCancel}
          onClickTab={onAuthenticationMethodChange}
          activeTab={authMethod}
          lastSeenUser={hasSavedUser ? savedUser : undefined}
          bannerComponent={customBannerComponentRef.current ?? <InfoBanner />}
          aboveHeaderBannerComponent={aboveHeaderBannerComponentRef.current}
          isOpen={isOpen}
          disableSuccessToast={disableSuccessToastRef.current}
          forceRedirectToCheckoutOnSSOLoginSuccess={
            forceRedirectToCheckoutOnAppleLoginSuccessRef.current
          }
        />
      )}
      {isAuthenticated && <AuthenticationValidatorLazy />}
      {isHydrationDone && isOpen && <LogLegacyFacebookStuckInPopup />}
      {isHydrationDone && (
        <SSOUrlCallbackHandlerLazy
          isSignUp={
            authMethod === 'register' || authMethod === 'guest-register'
          }
          onError={onError}
          onSuccess={onSuccess}
          disableSuccessToast={false}
        />
      )}
    </AuthenticationContext.Provider>
  );
};
